import debounce from 'lodash/debounce';
import uuid from 'uuid/v4';

import { type ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import type { ViewMode } from '@atlaskit/editor-plugin-editor-viewmode';
import { DecorationSet, type EditorView } from '@atlaskit/editor-prosemirror/view';

import type { AIDefinitionsPlugin } from '../../aiDefinitionsPluginType';
import type {
	AIDefinitionsPluginConfig,
	OpenReadingAidsPopupSource,
	ReadingAidsPopupPropsInState,
} from '../../types';
import {
	getAIDefinitionsErrorAnalyticsPayload,
	getOpenReadingAidsPopupAnalyticsPayload,
} from '../../ui/utils/analytics';

import { type AIDefinitionsAction, AIDefinitionsActionType } from './actions';
import { createCommand, getPluginState } from './plugin-factory';
import { createKeywordDecorationsForDoc } from './util/keyword';
import {
	resetKeywordStylerLoading,
	setupKeywordStylerMutationObserver,
	updateAllKeywordStylerPositions,
} from './util/keyword-styler';

export const updateKeywordRegex = ({ keywordRegex }: { keywordRegex: RegExp }) => {
	return createCommand<AIDefinitionsAction>({
		type: AIDefinitionsActionType.UPDATE_KEYWORD_REGEX,
		keywordRegex,
	});
};

export const updateKeywordDecorationsSessionId = () => {
	return createCommand<AIDefinitionsAction>({
		type: AIDefinitionsActionType.UPDATE_KEYWORD_DECORATIONS_SESSION_ID,
	});
};

export const openReadingAidsPopup = ({
	api,
	source,
	keywordId,
	readingAidsPopupProps,
}: {
	api: ExtractInjectionAPI<AIDefinitionsPlugin> | undefined;
	source: OpenReadingAidsPopupSource;
	keywordId?: string;
	readingAidsPopupProps: ReadingAidsPopupPropsInState;
}) => {
	const readingAidsSessionId = uuid();

	return createCommand<AIDefinitionsAction>(
		{
			type: AIDefinitionsActionType.OPEN_READING_AIDS_POPUP,
			keywordId,
			readingAidsPopupProps: { ...readingAidsPopupProps, sessionId: readingAidsSessionId },
		},
		(tr) => {
			if (api) {
				const wordCount = readingAidsPopupProps.selectedText.split(/\s+/u).length;
				const payload = getOpenReadingAidsPopupAnalyticsPayload({
					source,
					readingAidsSessionId,
					wordCount,
				});
				api.analytics?.actions.attachAnalyticsEvent(payload)(tr);
			}
			return tr;
		},
	);
};

export const closeReadingAidsPopup = () => {
	return createCommand<AIDefinitionsAction>({
		type: AIDefinitionsActionType.CLOSE_READING_AIDS_POPUP,
	});
};

export const readingAidsPopupLoaded = () => {
	return createCommand<AIDefinitionsAction>({
		type: AIDefinitionsActionType.READING_AIDS_POPUP_LOADED,
	});
};

export const updateKeywordDecorations = ({
	keywordDecorations,
}: {
	keywordDecorations: DecorationSet;
}) => {
	return createCommand<AIDefinitionsAction>({
		type: AIDefinitionsActionType.UPDATE_KEYWORD_DECORATIONS,
		keywordDecorations,
	});
};

export const removeKeywordDecorations = () => {
	return updateKeywordDecorations({ keywordDecorations: DecorationSet.empty });
};

function isKeywordDecorationsSessionIdChanged({
	editorView,
	keywordDecorationsSessionId,
}: {
	editorView: EditorView;
	keywordDecorationsSessionId: string;
}) {
	const aiDefinitionsPluginState = getPluginState(editorView.state);
	return keywordDecorationsSessionId !== aiDefinitionsPluginState.keywordDecorationsSessionId;
}

export const updateKeywordsAndDecorations = async ({
	api,
	config,
	editorView,
}: {
	api: ExtractInjectionAPI<AIDefinitionsPlugin> | undefined;
	config: AIDefinitionsPluginConfig;
	editorView: EditorView;
}) => {
	try {
		const aiDefinitionsPluginState = getPluginState(editorView.state);
		let { keywordRegex } = aiDefinitionsPluginState;

		// if keywordRegex is not available, fetch it and update decorations
		if (!keywordRegex) {
			const { keywordDecorationsSessionId } = aiDefinitionsPluginState;
			const res = await config.queryHighlightAcronyms?.({ cacheKey: keywordDecorationsSessionId });
			// if sessionId changed, abandon the response
			if (isKeywordDecorationsSessionIdChanged({ editorView, keywordDecorationsSessionId })) {
				return;
			}

			if (res && res.acronymRegex) {
				keywordRegex = res.acronymRegex;
				updateKeywordRegex({ keywordRegex })(editorView.state, editorView.dispatch);
				const keywordDecorations = createKeywordDecorationsForDoc({ editorView, keywordRegex });
				updateKeywordDecorations({ keywordDecorations })(editorView.state, editorView.dispatch);
			}
		}
	} catch (error) {
		api?.analytics?.actions.fireAnalyticsEvent(getAIDefinitionsErrorAnalyticsPayload({ error }));
	}
};

export const debouncedUpdateKeywordsAndDecorations = debounce(updateKeywordsAndDecorations, 1000);

/**
 *
 * @param root0
 * @param root0.editorView
 * @example
 */
export function handleCloseReadingAidsPopup({ editorView }: { editorView: EditorView }) {
	resetKeywordStylerLoading();
	closeReadingAidsPopup()(editorView.state, editorView.dispatch);
}

export const onLivePageModeChange = ({
	api,
	config,
	editorView,
	autoHighlightEnabled,
	livePageMode,
}: {
	api: ExtractInjectionAPI<AIDefinitionsPlugin> | undefined;
	config: AIDefinitionsPluginConfig;
	editorView: EditorView;
	autoHighlightEnabled?: boolean;
	livePageMode: ViewMode | undefined;
}) => {
	// we want to closeReadingAidsPopup if there is one opened
	handleCloseReadingAidsPopup({ editorView });

	// currently, only show auto-highlights in view mode
	if (autoHighlightEnabled && livePageMode === 'view') {
		const cleanObserver = setupKeywordStylerMutationObserver(editorView.dom);

		// when the decorations aren't changed, the DOM nodes are already inserted when `view` is called
		// - only happens on Hello (not locally)
		// so there must be time racing somehow
		// so update DOM nodes positions here to make sure they are all positioned correctly
		updateAllKeywordStylerPositions();

		updateKeywordsAndDecorations({ api, config, editorView });

		return cleanObserver;
	}
};
